import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  FormControlLabel,
  Checkbox,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tabs,
  Tab
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import axios from 'axios';
import config from '../config';
import RuleBasedReports from './RuleBasedReports';
import NewRuleBasedReports from './NewRuleBasedReports';
import AIRuleBasedReports from './AIRuleBasedReports';

const CallStatistics = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [startDate, setStartDate] = useState(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState(new Date());
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [agents, setAgents] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [showTotalCalls, setShowTotalCalls] = useState(true);
  const [showTotalDuration, setShowTotalDuration] = useState(true);
  const [chartHeight, setChartHeight] = useState(400);
  const [refreshKey, setRefreshKey] = useState(0); // Add refresh key to force re-render
  const [agentStatsData, setAgentStatsData] = useState(null);
  const [agentStatsLoading, setAgentStatsLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: 'duration',
    direction: 'desc'
  });

  const loadAgents = async () => {
    try {
      const response = await axios.get(`${config.apiBaseUrl}/api/agents`);
      // Agents API returns array directly, not wrapped in an object
      setAgents(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error loading agents:', error);
      if (error.response?.status === 401) {
        // Handle unauthorized access
        window.location.href = '/login';
      }
    }
  };

  const loadAgentStatistics = useCallback(async () => {
    // Only load agent statistics for admin users when "All Agents" is selected
    if (user?.role !== 'admin' || selectedAgent !== 'all') {
      setAgentStatsData(null);
      return;
    }

    setAgentStatsLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: format(startOfDay(startDate), 'yyyy-MM-dd'),
        endDate: format(endOfDay(endDate), 'yyyy-MM-dd')
      });

      const response = await axios.get(`${config.apiBaseUrl}/api/call-statistics/agent-statistics?${params}`, {
        headers: {
          'X-Cache-Bypass': refreshKey > 0 ? 'true' : 'false'
        }
      });

      if (response.data.success) {
        setAgentStatsData(response.data.data);
      } else {
        setAgentStatsData(null);
      }
    } catch (error) {
      console.error('Error loading agent statistics:', error);
      setAgentStatsData(null);
    } finally {
      setAgentStatsLoading(false);
    }
  }, [user, selectedAgent, startDate, endDate, refreshKey]);

  const loadStatistics = useCallback(async () => {
    console.log('Loading statistics with params:', { 
      startDate: format(startOfDay(startDate), 'yyyy-MM-dd'),
      endDate: format(endOfDay(endDate), 'yyyy-MM-dd'),
      selectedAgent,
      refreshKey,
      userRole: user?.role
    });
    
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        startDate: format(startOfDay(startDate), 'yyyy-MM-dd'),
        endDate: format(endOfDay(endDate), 'yyyy-MM-dd')
      });

      // Add agent filter only for admin users selecting specific agents
      // For agents, the backend automatically filters by their own data
      if (user?.role === 'admin' && selectedAgent !== 'all') {
        params.append('agentId', selectedAgent);
      }
      // Note: For agents, no agentId parameter is sent - backend handles filtering automatically

      // API call parameters prepared

      const response = await axios.get(`${config.apiBaseUrl}/api/call-statistics?${params}`, {
        headers: {
          // Add cache bypass header to ensure fresh data when refreshKey changes
          'X-Cache-Bypass': refreshKey > 0 ? 'true' : 'false'
        }
      });

      if (response.data.success) {
        console.log('Statistics loaded successfully:', {
          statistics: response.data.data.statistics,
          chartDataLength: response.data.data.chartData?.length || 0,
          selectedAgent
        });
        setStatistics(response.data.data.statistics);
        setChartData(response.data.data.chartData || []);
      } else {
        setError(response.data.message || 'Failed to load statistics');
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
      if (error.response?.status === 401) {
        // Handle unauthorized access
        window.location.href = '/login';
      } else {
        setError('Error loading statistics: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setLoading(false);
    }
  }, [user, startDate, endDate, selectedAgent, refreshKey]); // Add refreshKey to dependencies

  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem('user'));
    setUser(currentUser);
    
    // Only load agents and statistics if user is authenticated
    if (currentUser) {
      // Load agents if user is admin
      if (currentUser.role === 'admin') {
        loadAgents();
      }
    } else {
      // Redirect to login if no user
      window.location.href = '/login';
    }
  }, []);

  useEffect(() => {
    // Only load statistics if user is set and authenticated
    if (user) {
      loadStatistics();
    }
  }, [user, startDate, endDate, selectedAgent, loadStatistics]);

  useEffect(() => {
    // Only load top agents if user is set and authenticated
    if (user) {
      loadAgentStatistics();
    }
  }, [user, selectedAgent, startDate, endDate, loadAgentStatistics]);

  // Handle responsive chart height
  useEffect(() => {
    const updateChartHeight = () => {
      setChartHeight(window.innerWidth < 768 ? 300 : 400);
    };

    updateChartHeight();
    window.addEventListener('resize', updateChartHeight);
    return () => window.removeEventListener('resize', updateChartHeight);
  }, []);

  // Handle agent selection with refresh mechanism
  const handleAgentChange = (newAgentId) => {
    console.log('Agent selection changed:', { from: selectedAgent, to: newAgentId });
    setSelectedAgent(newAgentId);
    // Force refresh by incrementing refresh key
    setRefreshKey(prev => prev + 1);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0m 0s';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const formatChartDate = (dateString) => {
    return format(new Date(dateString), 'MMM dd');
  };

  // Custom tooltip component for better control
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Find the data point for this date to get top agents
      const dataPoint = chartData.find(item => item.date === label);
      const topAgents = dataPoint?.topAgents || [];
      
      return (
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid #ccc',
          borderRadius: '4px',
          padding: '12px',
          maxWidth: '300px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          <p style={{ 
            margin: '0 0 8px 0', 
            fontWeight: 'bold',
            fontSize: '14px',
            borderBottom: '1px solid #eee',
            paddingBottom: '4px'
          }}>
            {formatChartDate(label)}
          </p>
          
          {/* Chart metrics */}
          {payload.map((entry, index) => {
            let displayValue = entry.value;
            let displayName = entry.name;
            
            // Handle total duration formatting
            if (entry.dataKey === 'totalDuration') {
              const numValue = Number(entry.value);
              const hours = Math.floor(numValue / 3600);
              const minutes = Math.floor((numValue % 3600) / 60);
              const seconds = Math.floor(numValue % 60);
              
              if (hours > 0) {
                displayValue = `${hours}h ${minutes}m ${seconds}s`;
              } else if (minutes > 0) {
                displayValue = `${minutes}m ${seconds}s`;
              } else {
                displayValue = `${seconds}s`;
              }
              displayName = 'Total Duration';
            } else if (entry.dataKey === 'totalCalls') {
              displayName = 'Total Calls';
            }
            
            return (
              <p key={index} style={{ 
                margin: '4px 0', 
                color: entry.color,
                fontSize: '12px',
                fontWeight: 'bold'
              }}>
                {displayName}: {displayValue}
              </p>
            );
          })}
          
          {/* Top 5 Agents Section */}
          {topAgents.length > 0 && (
            <div style={{ 
              marginTop: '8px', 
              borderTop: '1px solid #eee',
              paddingTop: '8px'
            }}>
              <p style={{ 
                margin: '0 0 6px 0', 
                fontWeight: 'bold',
                fontSize: '12px',
                color: '#666'
              }}>
                Top 5 Agents:
              </p>
              {topAgents.map((agent, index) => {
                const agentDuration = formatDuration(agent.duration);
                return (
                  <div key={agent.agentId} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    margin: '2px 0',
                    fontSize: '11px',
                    padding: '2px 0'
                  }}>
                    <span style={{ 
                      fontWeight: 'bold',
                      color: '#333',
                      flex: '1'
                    }}>
                      {index + 1}. {agent.agentName}
                    </span>
                    <span style={{ 
                      color: '#666',
                      marginLeft: '8px',
                      textAlign: 'right'
                    }}>
                      {agent.calls} calls ({agentDuration})
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  // Sorting function
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get sorted agents data
  const getSortedAgents = () => {
    if (!agentStatsData || !agentStatsData.agents) return [];
    
    return [...agentStatsData.agents].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'agentName':
          aValue = a.agentName.toLowerCase();
          bValue = b.agentName.toLowerCase();
          break;
        case 'calls':
          aValue = a.calls;
          bValue = b.calls;
          break;
        case 'duration':
          aValue = a.duration;
          bValue = b.duration;
          break;
        case 'agentDuration':
          aValue = a.agentDuration || 0;
          bValue = b.agentDuration || 0;
          break;
        case 'transferDuration':
          aValue = a.transferDuration || 0;
          bValue = b.transferDuration || 0;
          break;
        case 'transferCalls':
          aValue = a.transferCalls || 0;
          bValue = b.transferCalls || 0;
          break;
        default:
          aValue = a.duration;
          bValue = b.duration;
      }
      
      if (sortConfig.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  // Sort icon component
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <span style={{ marginLeft: '4px', color: '#ccc' }}>↕</span>;
    }
    return (
      <span style={{ marginLeft: '4px', color: '#1976d2' }}>
        {sortConfig.direction === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // For admin users, show tabs
  if (user && user.role === 'admin') {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Paper sx={{ mb: 3 }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="statistics tabs">
              <Tab label="Call Statistics" />
              <Tab label="Rule-Based Reports" />
              <Tab label="New Rule Based Reports" />
              <Tab label="AI Rule Report" />
            </Tabs>
          </Paper>

          {activeTab === 1 ? (
            <RuleBasedReports />
          ) : activeTab === 2 ? (
            <NewRuleBasedReports />
          ) : activeTab === 3 ? (
            <AIRuleBasedReports />
          ) : (
            <>
              {/* Filters */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={(newValue) => setStartDate(newValue)}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                      maxDate={endDate}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={(newValue) => setEndDate(newValue)}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                      minDate={startDate}
                      maxDate={new Date()}
                    />
                  </Grid>
                  {user?.role === 'admin' && (
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth>
                        <InputLabel>Agent</InputLabel>
                        <Select
                          value={selectedAgent}
                          label="Agent"
                          onChange={(e) => handleAgentChange(e.target.value)}
                        >
                          <MenuItem value="all">All Agents</MenuItem>
                          {agents.map((agent) => (
                            <MenuItem key={agent._id} value={agent.customAgentId}>
                              {agent.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* Statistics Cards */}
              {loading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              ) : statistics ? (
                <>
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>
                            Total Calls
                          </Typography>
                          <Typography variant="h4" component="div">
                            {statistics.totalCalls}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>
                            Average Duration
                          </Typography>
                          <Typography variant="h4" component="div">
                            {formatDuration(statistics.averageDuration)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>
                            Total Duration
                          </Typography>
                          <Typography variant="h4" component="div">
                            {formatDuration(statistics.totalDuration)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    {user && user.role === 'admin' && (
                      <>
                        <Grid item xs={12} sm={6} md={4}>
                          <Card>
                            <CardContent>
                              <Typography color="textSecondary" gutterBottom>
                                Agent Duration
                              </Typography>
                              <Typography variant="h4" component="div">
                                {formatDuration(statistics.agentDuration || 0)}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Card>
                            <CardContent>
                              <Typography color="textSecondary" gutterBottom>
                                Transfer Duration
                              </Typography>
                              <Typography variant="h4" component="div">
                                {formatDuration(statistics.transferDuration || 0)}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Card>
                            <CardContent>
                              <Typography color="textSecondary" gutterBottom>
                                Transfer Calls
                              </Typography>
                              <Typography variant="h4" component="div">
                                {statistics.transferCalls || 0}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </>
                    )}
                  </Grid>

                  {/* Chart */}
                  {chartData && chartData.length > 0 ? (
                    <Paper sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        Call Statistics Over Time
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={showTotalCalls}
                                onChange={(e) => setShowTotalCalls(e.target.checked)}
                                sx={{
                                  '&.Mui-checked': {
                                    color: '#8884d8',
                                  },
                                }}
                              />
                            }
                            label="Total Calls"
                            sx={{ 
                              '& .MuiFormControlLabel-label': { 
                                color: '#8884d8',
                                fontWeight: 'bold'
                              } 
                            }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={showTotalDuration}
                                onChange={(e) => setShowTotalDuration(e.target.checked)}
                                sx={{
                                  '&.Mui-checked': {
                                    color: '#82ca9d',
                                  },
                                }}
                              />
                            }
                            label="Total Duration"
                            sx={{ 
                              '& .MuiFormControlLabel-label': { 
                                color: '#82ca9d',
                                fontWeight: 'bold'
                              } 
                            }}
                          />
                        </Box>
                        <Typography variant="caption" color="textSecondary">
                          {chartData.length} days of data
                        </Typography>
                      </Box>
                      <ResponsiveContainer width="100%" height={chartHeight}>
                        <LineChart data={chartData} key={refreshKey}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="date" 
                            tickFormatter={formatChartDate}
                            angle={chartHeight < 350 ? -45 : -45}
                            textAnchor="end"
                            height={chartHeight < 350 ? 60 : 80}
                            tick={{ fontSize: chartHeight < 350 ? 10 : 12 }}
                            interval="preserveStartEnd"
                          />
                          <YAxis 
                            yAxisId="left" 
                            label={{ value: 'Total Calls', angle: -90, position: 'insideLeft' }}
                            tick={{ fontSize: chartHeight < 350 ? 10 : 12 }}
                          />
                          <YAxis 
                            yAxisId="right" 
                            orientation="right"
                            label={{ value: 'Duration (hours)', angle: 90, position: 'insideRight' }}
                            tickFormatter={(value) => {
                              const hours = Math.floor(value / 3600);
                              const minutes = Math.floor((value % 3600) / 60);
                              return hours > 0 ? `${hours}h` : `${minutes}m`;
                            }}
                            tick={{ fontSize: chartHeight < 350 ? 10 : 12 }}
                          />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          {showTotalCalls && (
                            <Line 
                              yAxisId="left"
                              type="monotone"
                              dataKey="totalCalls" 
                              stroke="#8884d8" 
                              strokeWidth={2}
                              dot={{ fill: '#8884d8', strokeWidth: 2, r: 4 }}
                              activeDot={{ r: 6 }}
                              name="Total Calls"
                            />
                          )}
                          {showTotalDuration && (
                            <Line 
                              yAxisId="right"
                              type="monotone"
                              dataKey="totalDuration" 
                              stroke="#82ca9d" 
                              strokeWidth={2}
                              dot={{ fill: '#82ca9d', strokeWidth: 2, r: 4 }}
                              activeDot={{ r: 6 }}
                              name="Total Duration"
                            />
                          )}
                        </LineChart>
                      </ResponsiveContainer>
                    </Paper>
                  ) : (
                    <Paper sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="body1" color="textSecondary">
                        No chart data available for the selected date range
                      </Typography>
                    </Paper>
                  )}

                  {/* Agent Statistics Section - Admin Only */}
                  {user?.role === 'admin' && selectedAgent === 'all' && (
                    <Paper sx={{ p: 3, mt: 3 }}>
                      <Typography variant="h6" gutterBottom sx={{ 
                        color: '#1976d2', 
                        fontWeight: 'bold',
                        borderBottom: '2px solid #1976d2',
                        paddingBottom: '8px',
                        marginBottom: '16px'
                      }}>
                        📊 Agent Statistics
                      </Typography>
                      
                      {agentStatsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                          <CircularProgress />
                        </Box>
                      ) : agentStatsData && agentStatsData.agents && agentStatsData.agents.length > 0 ? (
                        <>
                          <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="body2" color="textSecondary">
                              <strong>Date Range:</strong> {format(startOfDay(startDate), 'MMM dd, yyyy')} - {format(endOfDay(endDate), 'MMM dd, yyyy')}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              <strong>Total Calls:</strong> {agentStatsData.totalCalls} | <strong>Total Duration:</strong> {formatDuration(agentStatsData.totalDuration || 0)} | <strong>Agent Duration:</strong> {formatDuration(agentStatsData.agentDuration || 0)} | <strong>Transfer Duration:</strong> {formatDuration(agentStatsData.transferDuration || 0)}
                            </Typography>
                          </Box>
                          
                          <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ bgcolor: '#1976d2' }}>
                                  <TableCell 
                                    sx={{ color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
                                    onClick={() => handleSort('agentName')}
                                  >
                                    Agent Name <SortIcon columnKey="agentName" />
                                  </TableCell>
                                  <TableCell 
                                    sx={{ color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
                                    align="right"
                                    onClick={() => handleSort('calls')}
                                  >
                                    Total Calls <SortIcon columnKey="calls" />
                                  </TableCell>
                                  <TableCell 
                                    sx={{ color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
                                    align="right"
                                    onClick={() => handleSort('duration')}
                                  >
                                    Total Duration <SortIcon columnKey="duration" />
                                  </TableCell>
                                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                                    Avg Duration
                                  </TableCell>
                                  <TableCell 
                                    sx={{ color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
                                    align="right"
                                    onClick={() => handleSort('agentDuration')}
                                  >
                                    Agent Duration <SortIcon columnKey="agentDuration" />
                                  </TableCell>
                                  <TableCell 
                                    sx={{ color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
                                    align="right"
                                    onClick={() => handleSort('transferDuration')}
                                  >
                                    Transfer Duration <SortIcon columnKey="transferDuration" />
                                  </TableCell>
                                  <TableCell 
                                    sx={{ color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
                                    align="right"
                                    onClick={() => handleSort('transferCalls')}
                                  >
                                    Transfer Calls <SortIcon columnKey="transferCalls" />
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {getSortedAgents().map((agent) => {
                                  const avgDuration = agent.calls > 0 ? Math.round(agent.duration / agent.calls) : 0;
                                  return (
                                    <TableRow 
                                      key={agent.agentId}
                                      sx={{ 
                                        '&:nth-of-type(odd)': { bgcolor: '#fafafa' },
                                        '&:hover': { bgcolor: '#f0f8ff' }
                                      }}
                                    >
                                      <TableCell sx={{ fontWeight: 'bold' }}>
                                        {agent.agentName}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 'bold', color: '#8884d8' }}>
                                        {agent.calls}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 'bold', color: '#82ca9d' }}>
                                        {formatDuration(agent.duration)}
                                      </TableCell>
                                      <TableCell align="right" sx={{ color: '#666' }}>
                                        {formatDuration(avgDuration)}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                                        {formatDuration(agent.agentDuration || 0)}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>
                                        {formatDuration(agent.transferDuration || 0)}
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 'bold', color: '#f44336' }}>
                                        {agent.transferCalls || 0}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </>
                      ) : (
                        <Typography variant="body2" color="textSecondary" sx={{ p: 3, textAlign: 'center' }}>
                          No agent statistics available for the selected date range
                        </Typography>
                      )}
                    </Paper>
                  )}
                </>
              ) : null}
            </>
          )}
        </Container>
      </LocalizationProvider>
    );
  }

  // For non-admin users (agents), show only Call Statistics
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Page Header for Agents */}
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
          Call Statistics
        </Typography>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
                maxDate={endDate}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
                minDate={startDate}
                maxDate={new Date()}
              />
            </Grid>
            {user?.role === 'admin' && (
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Agent</InputLabel>
                  <Select
                    value={selectedAgent}
                    label="Agent"
                    onChange={(e) => handleAgentChange(e.target.value)}
                  >
                    <MenuItem value="all">All Agents</MenuItem>
                    {agents.map((agent) => (
                      <MenuItem key={agent._id} value={agent.customAgentId}>
                        {agent.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            {user?.role === 'agent' && (
              <Grid item xs={12} sm={6} md={3}>
                <Chip 
                  label={`Agent: ${user.name || 'You'}`} 
                  color="primary" 
                  variant="outlined"
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Statistics Cards */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : statistics ? (
          <>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Calls
                    </Typography>
                    <Typography variant="h4" component="div">
                      {statistics.totalCalls}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Average Duration
                    </Typography>
                    <Typography variant="h4" component="div">
                      {formatDuration(statistics.averageDuration)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Duration
                    </Typography>
                    <Typography variant="h4" component="div">
                      {formatDuration(statistics.totalDuration)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

                         {/* Chart */}
             {chartData && chartData.length > 0 ? (
               <Paper sx={{ p: 3 }}>
                 <Typography variant="h6" gutterBottom>
                   Call Statistics Over Time
                 </Typography>
                 <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                   <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                     <FormControlLabel
                       control={
                         <Checkbox
                           checked={showTotalCalls}
                           onChange={(e) => setShowTotalCalls(e.target.checked)}
                           sx={{
                             '&.Mui-checked': {
                               color: '#8884d8',
                             },
                           }}
                         />
                       }
                       label="Total Calls"
                       sx={{ 
                         '& .MuiFormControlLabel-label': { 
                           color: '#8884d8',
                           fontWeight: 'bold'
                         } 
                       }}
                     />
                     <FormControlLabel
                       control={
                         <Checkbox
                           checked={showTotalDuration}
                           onChange={(e) => setShowTotalDuration(e.target.checked)}
                           sx={{
                             '&.Mui-checked': {
                               color: '#82ca9d',
                             },
                           }}
                         />
                       }
                       label="Total Duration"
                       sx={{ 
                         '& .MuiFormControlLabel-label': { 
                           color: '#82ca9d',
                           fontWeight: 'bold'
                         } 
                       }}
                     />
                   </Box>
                   <Typography variant="caption" color="textSecondary">
                     {chartData.length} days of data
                   </Typography>
                 </Box>
                 <ResponsiveContainer width="100%" height={chartHeight}>
                   <LineChart data={chartData} key={refreshKey}>
                     <CartesianGrid strokeDasharray="3 3" />
                     <XAxis 
                       dataKey="date" 
                       tickFormatter={formatChartDate}
                       angle={chartHeight < 350 ? -45 : -45}
                       textAnchor="end"
                       height={chartHeight < 350 ? 60 : 80}
                       tick={{ fontSize: chartHeight < 350 ? 10 : 12 }}
                       interval="preserveStartEnd"
                     />
                     <YAxis 
                       yAxisId="left" 
                       label={{ value: 'Total Calls', angle: -90, position: 'insideLeft' }}
                       tick={{ fontSize: chartHeight < 350 ? 10 : 12 }}
                     />
                     <YAxis 
                       yAxisId="right" 
                       orientation="right"
                       label={{ value: 'Duration (hours)', angle: 90, position: 'insideRight' }}
                       tickFormatter={(value) => {
                         const hours = Math.floor(value / 3600);
                         const minutes = Math.floor((value % 3600) / 60);
                         return hours > 0 ? `${hours}h` : `${minutes}m`;
                       }}
                       tick={{ fontSize: chartHeight < 350 ? 10 : 12 }}
                     />
                     <Tooltip content={<CustomTooltip />} />
                     <Legend />
                     {showTotalCalls && (
                       <Line 
                         yAxisId="left"
                         type="monotone"
                         dataKey="totalCalls" 
                         stroke="#8884d8" 
                         strokeWidth={2}
                         dot={{ fill: '#8884d8', strokeWidth: 2, r: 4 }}
                         activeDot={{ r: 6 }}
                         name="Total Calls"
                       />
                     )}
                     {showTotalDuration && (
                       <Line 
                         yAxisId="right"
                         type="monotone"
                         dataKey="totalDuration" 
                         stroke="#82ca9d" 
                         strokeWidth={2}
                         dot={{ fill: '#82ca9d', strokeWidth: 2, r: 4 }}
                         activeDot={{ r: 6 }}
                         name="Total Duration"
                       />
                     )}
                   </LineChart>
                 </ResponsiveContainer>
               </Paper>
             ) : (
               <Paper sx={{ p: 3, textAlign: 'center' }}>
                 <Typography variant="body1" color="textSecondary">
                   No chart data available for the selected date range
                 </Typography>
               </Paper>
             )}

             {/* Agent Statistics Section - Admin Only */}
             {user?.role === 'admin' && selectedAgent === 'all' && (
               <Paper sx={{ p: 3, mt: 3 }}>
                 <Typography variant="h6" gutterBottom sx={{ 
                   color: '#1976d2', 
                   fontWeight: 'bold',
                   borderBottom: '2px solid #1976d2',
                   paddingBottom: '8px',
                   marginBottom: '16px'
                 }}>
                   📊 Agent Statistics
                 </Typography>
                 
                 {agentStatsLoading ? (
                   <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                     <CircularProgress />
                   </Box>
                 ) : agentStatsData && agentStatsData.agents && agentStatsData.agents.length > 0 ? (
                   <>
                     <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                       <Typography variant="body2" color="textSecondary">
                         <strong>Date Range:</strong> {format(startOfDay(startDate), 'MMM dd, yyyy')} - {format(endOfDay(endDate), 'MMM dd, yyyy')}
                       </Typography>
                       <Typography variant="body2" color="textSecondary">
                         <strong>Total Calls:</strong> {agentStatsData.totalCalls} | <strong>Total Duration:</strong> {formatDuration(agentStatsData.totalDuration || 0)} | <strong>Agent Duration:</strong> {formatDuration(agentStatsData.agentDuration || 0)} | <strong>Transfer Duration:</strong> {formatDuration(agentStatsData.transferDuration || 0)}
                       </Typography>
                     </Box>
                     
                     <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                       <Table size="small">
                         <TableHead>
                           <TableRow sx={{ bgcolor: '#1976d2' }}>
                             <TableCell 
                               sx={{ color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
                               onClick={() => handleSort('agentName')}
                             >
                               Agent Name <SortIcon columnKey="agentName" />
                             </TableCell>
                             <TableCell 
                               sx={{ color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
                               align="right"
                               onClick={() => handleSort('calls')}
                             >
                               Total Calls <SortIcon columnKey="calls" />
                             </TableCell>
                             <TableCell 
                               sx={{ color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
                               align="right"
                               onClick={() => handleSort('duration')}
                             >
                               Total Duration <SortIcon columnKey="duration" />
                             </TableCell>
                             <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                               Avg Duration
                             </TableCell>
                             <TableCell 
                               sx={{ color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
                               align="right"
                               onClick={() => handleSort('agentDuration')}
                             >
                               Agent Duration <SortIcon columnKey="agentDuration" />
                             </TableCell>
                             <TableCell 
                               sx={{ color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
                               align="right"
                               onClick={() => handleSort('transferDuration')}
                             >
                               Transfer Duration <SortIcon columnKey="transferDuration" />
                             </TableCell>
                             <TableCell 
                               sx={{ color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
                               align="right"
                               onClick={() => handleSort('transferCalls')}
                             >
                               Transfer Calls <SortIcon columnKey="transferCalls" />
                             </TableCell>
                           </TableRow>
                         </TableHead>
                         <TableBody>
                           {getSortedAgents().map((agent) => {
                             const avgDuration = agent.calls > 0 ? Math.round(agent.duration / agent.calls) : 0;
                             return (
                               <TableRow 
                                 key={agent.agentId}
                                 sx={{ 
                                   '&:nth-of-type(odd)': { bgcolor: '#fafafa' },
                                   '&:hover': { bgcolor: '#f0f8ff' }
                                 }}
                               >
                                 <TableCell sx={{ fontWeight: 'bold' }}>
                                   {agent.agentName}
                                 </TableCell>
                                 <TableCell align="right" sx={{ fontWeight: 'bold', color: '#8884d8' }}>
                                   {agent.calls}
                                 </TableCell>
                                 <TableCell align="right" sx={{ fontWeight: 'bold', color: '#82ca9d' }}>
                                   {formatDuration(agent.duration)}
                                 </TableCell>
                                 <TableCell align="right" sx={{ color: '#666' }}>
                                   {formatDuration(avgDuration)}
                                 </TableCell>
                                 <TableCell align="right" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                                   {formatDuration(agent.agentDuration || 0)}
                                 </TableCell>
                                 <TableCell align="right" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>
                                   {formatDuration(agent.transferDuration || 0)}
                                 </TableCell>
                                 <TableCell align="right" sx={{ fontWeight: 'bold', color: '#f44336' }}>
                                   {agent.transferCalls || 0}
                                 </TableCell>
                               </TableRow>
                             );
                           })}
                         </TableBody>
                       </Table>
                     </TableContainer>
                   </>
                 ) : (
                   <Box sx={{ textAlign: 'center', p: 3 }}>
                     <Typography variant="body1" color="textSecondary">
                       No agent data available for the selected date range
                     </Typography>
                   </Box>
                 )}
               </Paper>
             )}
          </>
        ) : null}
      </Container>
    </LocalizationProvider>
  );
};

export default CallStatistics;
